/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */
import React from 'react';

import { jsx } from '@atlaskit/css';
import { Popup } from '@atlaskit/popup/popup';
import type { ContentProps } from '@atlaskit/popup/types';

import { type NLPSearchResultSource } from '../../../ai-answer-dialog/types';
import { SourcesBar } from '../sources-button/sources-bar';
import { extractProductNameFromARI } from './ari';
import { type OnClickSource } from './create-fallback-component';
import { SourcesPopupContent } from './SourcesPopupContent';
import { useRepositionOnLayoutShift } from './use-reposition-on-layout-shift';

export type SourcesPopupWithBarTriggerProps = {
	/** Sources to render in the popup. Malformed/empty URLs are filtered out. */
	sources: NLPSearchResultSource[];
	/** Optional callback invoked when a source link is clicked. */
	onClickSource?: OnClickSource;
	/** Optional callback invoked the first time the sources popup is opened. */
	onSourcesButtonClicked?: () => void;
	/**
	 * Controls whether the popup renders into its parent (vs a portal). Defaults
	 * to `false` so the popup portals above positioned siblings such as the chat
	 * input box (which establishes its own stacking context).
	 */
	shouldRenderPopupToParent?: boolean;
	/** Opens source links in the same tab instead of a new one. */
	openSourceLinkInSameTab?: boolean;
};

type SourcesPopupContentWithRepositionProps = {
	update: ContentProps['update'];
	isOpen: boolean;
	sources: NLPSearchResultSource[];
	onClickSourceHandler: (
		type: string,
		ari?: string | null,
		product?: string | null,
		index?: number,
	) => void;
	onClickSource?: OnClickSource;
	onClose: () => void;
	openSourceLinkInSameTab: boolean;
};

/**
 * Wraps {@link SourcesPopupContent} so the popup repositions to follow its
 * trigger when the page layout shifts (e.g. Rovo Chat opening a side panel).
 */
const SourcesPopupContentWithReposition = ({
	update,
	isOpen,
	...contentProps
}: SourcesPopupContentWithRepositionProps): JSX.Element => {
	useRepositionOnLayoutShift(isOpen, update);
	return <SourcesPopupContent {...contentProps} />;
};

const isRenderableSource = (source: NLPSearchResultSource): boolean => {
	if (!source?.url) {
		return false;
	}
	try {
		new URL(source.url);
		return true;
	} catch {
		return false;
	}
};

/**
 * Renders the existing {@link SourcesBar} button as the trigger for the sources
 * popup. Clicking the bar toggles a scrollable popup listing the sources used to
 * generate an answer (ported from the Rovo Chat conversation assistant).
 */
export const SourcesPopupWithBarTrigger = ({
	sources,
	onClickSource,
	onSourcesButtonClicked,
	shouldRenderPopupToParent = false,
	openSourceLinkInSameTab = false,
}: SourcesPopupWithBarTriggerProps): JSX.Element | null => {
	const [isPopupOpen, setIsPopupOpen] = React.useState(false);

	const validSources = React.useMemo(() => (sources ?? []).filter(isRenderableSource), [sources]);

	const onClickSourceHandler = React.useCallback(
		(type: string, ari?: string | null, product?: string | null, index?: number) => {
			if (onClickSource) {
				onClickSource(type, extractProductNameFromARI(ari), product ?? null, index);
			}
		},
		[onClickSource],
	);

	const handlePopupOpenChange = React.useCallback(
		(nextIsPopupOpen: boolean) => {
			if (nextIsPopupOpen && !isPopupOpen) {
				onSourcesButtonClicked?.();
			}
			setIsPopupOpen(nextIsPopupOpen);
		},
		[isPopupOpen, onSourcesButtonClicked],
	);

	if (validSources.length === 0) {
		return null;
	}

	return (
		<Popup
			placement="top-start"
			isOpen={isPopupOpen}
			onClose={() => handlePopupOpenChange(false)}
			shouldRenderToParent={shouldRenderPopupToParent}
			content={({ update }) => (
				<SourcesPopupContentWithReposition
					update={update}
					isOpen={isPopupOpen}
					sources={validSources}
					onClickSourceHandler={onClickSourceHandler}
					onClickSource={onClickSource}
					onClose={() => handlePopupOpenChange(false)}
					openSourceLinkInSameTab={openSourceLinkInSameTab}
				/>
			)}
			trigger={(triggerProps) => (
				<SourcesBar
					sources={validSources}
					numSources={validSources.length}
					onClick={() => handlePopupOpenChange(!isPopupOpen)}
					triggerProps={triggerProps}
					isSelected={isPopupOpen}
				/>
			)}
		/>
	);
};
